import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Ray Gift Wrap
 *
 * Cart-only "add gift wrap" checkbox (snippets/ray-gift-wrap.liquid). Box size
 * (small/large variant) is decided server-side on render, then kept in sync here:
 * - checkbox toggle adds/removes the recommended variant via the Cart AJAX API
 * - a chevron button expands/collapses the description panel (no network)
 * - every cart:update (from anywhere - line qty change, other add-to-cart, this
 *   widget itself) re-checks whether the box already in the cart still matches what
 *   the current subtotal recommends, and swaps it if the subtotal crossed the
 *   threshold since it was added
 */
const SOURCE_ID = 'ray-gift-wrap';

const selectors = {
  wrap: '[data-ray-gift-wrap]',
  checkbox: '[data-ray-gift-wrap-checkbox]',
  toggle: '[data-ray-gift-wrap-toggle]',
};

let isSyncing = false;

const cartRequest = async (url, body) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`Ray gift wrap: ${url} failed (${response.status})`);
  }

  return response.json();
};

const addVariant = (variantId) => cartRequest('/cart/add.js', { items: [{ id: Number(variantId), quantity: 1 }] });
const removeLine = (lineKey) => cartRequest('/cart/change.js', { id: lineKey, quantity: 0 });

const removeWrapLine = async (smallId, largeId) => {
  const cart = await fetchCart();
  const item = cart.items.find(
    (line) => String(line.variant_id) === smallId || String(line.variant_id) === largeId
  );

  return item ? removeLine(item.key) : cart;
};

const fetchCart = async () => {
  const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });

  if (!response.ok) {
    throw new Error(`Ray gift wrap: /cart.js failed (${response.status})`);
  }

  return response.json();
};

const announceCartUpdate = (cart) => {
  document.dispatchEvent(
    new CartUpdateEvent(cart, SOURCE_ID, {
      source: SOURCE_ID,
      itemCount: cart.item_count,
    })
  );
};

const handleToggle = (event) => {
  const toggle = event.target.closest(selectors.toggle);

  if (!toggle) return;

  const wrap = toggle.closest(selectors.wrap);

  if (!wrap) return;

  const isOpen = wrap.getAttribute('data-open') === 'true';

  wrap.setAttribute('data-open', isOpen ? 'false' : 'true');
  toggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
};

const handleCheckboxChange = async (event) => {
  const checkbox = event.target.closest(selectors.checkbox);

  if (!checkbox) return;

  const wrap = checkbox.closest(selectors.wrap);

  if (!wrap) return;

  checkbox.disabled = true;

  try {
    let cart;

    if (checkbox.checked) {
      await addVariant(wrap.dataset.recommendedVariantId);
      cart = await fetchCart();
    } else {
      cart = await removeWrapLine(wrap.dataset.smallVariantId, wrap.dataset.largeVariantId);
    }

    announceCartUpdate(cart);
  } catch (error) {
    console.error('Ray gift wrap: failed to update cart', error);
    checkbox.checked = !checkbox.checked;
    checkbox.disabled = false;
  }
};

/**
 * Re-evaluates the box size already in the cart against the current subtotal, and
 * swaps the variant if they no longer match.
 * @param {Object} cart - Shopify cart object (from a cart:update event).
 */
const reconcile = async (cart) => {
  if (isSyncing || !cart || !Array.isArray(cart.items)) return;

  const wrap = document.querySelector(selectors.wrap);

  if (!wrap) return;

  const smallId = wrap.dataset.smallVariantId;
  const largeId = wrap.dataset.largeVariantId;
  const thresholdCents = Number(wrap.dataset.thresholdCents || 0);

  const wrapItem = cart.items.find(
    (item) => String(item.variant_id) === smallId || String(item.variant_id) === largeId
  );

  if (!wrapItem) return;

  if (cart.items.length === 1) {
    isSyncing = true;

    try {
      announceCartUpdate(await removeWrapLine(smallId, largeId));
    } catch (error) {
      console.error('Ray gift wrap: failed to remove gift wrap', error);
    } finally {
      isSyncing = false;
    }

    return;
  }

  const subtotalExclWrap = cart.items_subtotal_price - wrapItem.final_line_price;
  const recommendedId = thresholdCents > 0 && subtotalExclWrap > thresholdCents ? largeId : smallId;

  if (String(wrapItem.variant_id) === recommendedId) return;

  isSyncing = true;

  try {
    await removeWrapLine(smallId, largeId);
    await addVariant(recommendedId);

    announceCartUpdate(await fetchCart());
  } catch (error) {
    console.error('Ray gift wrap: failed to switch box size', error);
  } finally {
    isSyncing = false;
  }
};

document.addEventListener('click', handleToggle);
document.addEventListener('change', handleCheckboxChange);

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.sourceId === SOURCE_ID) return;

  reconcile(event.detail?.resource);
});

document.addEventListener(ThemeEvents.discountUpdate, (event) => {
  reconcile(event.detail?.resource);
});
